import { Link } from "@tanstack/react-router";
import { PlugZap, TriangleAlert, Users } from "lucide-react";
import { useMemo, useState } from "react";
import { PersonAvatar } from "@/components/persons/PersonAvatar";
import { useSetBreadcrumbs } from "@/components/shared/breadcrumbs";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { PageHeader } from "@/components/shared/page-header";
import { TeamAttention } from "@/components/team/TeamAttention";
import { TeamFilterPanel } from "@/components/team/TeamFilterPanel";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { useTeamSummary } from "@/hooks/use-team-summary";
import { pageOriginSearch } from "@/lib/detail-origin";
import { currentMonthKey, labelWindow, UNDER_GUIDED_PERCENT } from "@/lib/persons/project-guidance";
import { unassessedReports } from "@/lib/persons/team-attention";
import {
  EMPTY_TEAM_FILTERS,
  hasTeamFilters,
  matchesTeamFilters,
  type TeamFilters,
} from "@/lib/persons/team-filters";
import {
  billableSharePercent,
  defaultLoggedMonth,
  emptyTimeTotals,
  isBookedPeriod,
  isUnderGuided,
  type TeamGuidanceMonth,
  type TeamMemberSummary,
  type TeamScope,
  TIME_BUCKET_LABELS,
  TIME_BUCKETS,
  type TimeBucket,
  type TimeTotals,
  totalLogged,
} from "@/lib/persons/team-summary";
import { cn } from "@/lib/utils";

/**
 * The figures carry this table's emphasis and everything else recedes — 41 of
 * its 96 cells hold no value, so a uniform weight spent the reader's attention
 * on dashes. Labels drop to 11px and empties to 40% opacity; the em-dash stays
 * rather than emptying the cell, because a blank cell reads as a fault.
 */
const FIGURE = "text-[15px] font-semibold tabular-nums";
const EMPTY = "text-muted-foreground/40";

/** `322.5` → `"322.5"`, `96` → `"96"`. Bare, like the Current Projects strip. */
function hoursValue(hours: number): string {
  return String(hours % 1 === 0 ? hours : hours.toFixed(1));
}

/**
 * Switches the report between the viewer's own team and the whole business.
 *
 * Offered to admins only, and rendered even while the company payload is
 * loading — a control that vanishes mid-request reads as a failure, and the
 * skeleton below already says something is happening.
 */
function ScopeSwitch({
  scope,
  onChange,
}: {
  scope: TeamScope;
  onChange: (scope: TeamScope) => void;
}) {
  return (
    <fieldset className="inline-flex rounded-lg border p-0.5" aria-label="Report scope">
      {(["team", "company"] as const).map((value) => (
        <button
          key={value}
          type="button"
          aria-pressed={scope === value}
          onClick={() => onChange(value)}
          className={cn(
            "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
            scope === value
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {value === "team" ? "My Team" : "Entire Company"}
        </button>
      ))}
    </fieldset>
  );
}

export function TeamPage({
  managerId,
  managerName,
  /** True when the viewer is looking at their OWN team. */
  isSelf,
  /**
   * Whether to offer the company-wide scope — admins only, and only on their
   * OWN team page. A named manager's team, reached from their profile, is about
   * that manager, so widening it there would answer a question nobody asked.
   */
  canSwitchScope = false,
}: {
  managerId: string;
  managerName: string | null;
  isSelf: boolean;
  canSwitchScope?: boolean;
}) {
  useSetBreadcrumbs([{ label: "People & Organization" }, { label: "My Team" }]);
  const [scope, setScope] = useState<TeamScope>("team");
  // Never ask for company scope without the affordance to leave it.
  const effectiveScope: TeamScope = canSwitchScope ? scope : "team";
  const { data, isLoading, isError, error, refetch } = useTeamSummary(managerId, effectiveScope);

  const [filters, setFilters] = useState<TeamFilters>(EMPTY_TEAM_FILTERS);
  const [search, setSearch] = useState("");

  const allMembers = data?.members ?? [];
  const labels = data ? labelWindow(data.window) : [];
  const current = currentMonthKey();
  // What we RECEIVED, not what we asked for — they differ for a moment on every
  // switch, and labelling the table by the request would caption the old rows
  // with the new scope.
  const shown = data?.scope ?? effectiveScope;
  const isCompany = shown === "company";

  // Filtering is client-side over the payload already in hand. The roster
  // arrives in one response and the gateway calls behind it are the expensive
  // part, so re-fetching per filter combination would pay for them again to
  // produce a subset of what is already here.
  //
  // Company scope only: the controls are not offered on a manager's own team,
  // and leaving them applied after a switch would silently hide reports.
  const members = useMemo(
    () =>
      isCompany ? allMembers.filter((m) => matchesTeamFilters(m, filters, search)) : allMembers,
    [allMembers, isCompany, filters, search],
  );
  // The attention block and the "no figures in NOS" footnote below have to
  // describe the people ON SCREEN. A block flagging someone the filter removed
  // is worse than no block — the name is not there to click.
  const visibleIds = useMemo(() => new Set(members.map((m) => m.personId)), [members]);
  const attention = useMemo(
    () => data?.attention?.filter((e) => visibleIds.has(e.personId)) ?? null,
    [data?.attention, visibleIds],
  );
  const filtering = isCompany && hasTeamFilters(filters, search);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="People & Organization"
        title={isCompany ? "Entire Company" : isSelf ? "My Team" : `${managerName ?? "Team"}`}
        description={
          isCompany
            ? "Guidance and logged time for everyone still working here."
            : isSelf
              ? "Guidance and logged time for everyone who reports to you."
              : `Guidance and logged time for everyone who reports to ${managerName ?? "this person"}.`
        }
      />

      {canSwitchScope && (
        <ScopeSwitch
          scope={scope}
          onChange={(next) => {
            setScope(next);
            // Leaving the company view drops the filters with it. They are only
            // offered there, so carrying them back would hide reports with
            // controls the viewer can no longer see.
            if (next !== "company") {
              setFilters(EMPTY_TEAM_FILTERS);
              setSearch("");
            }
          }}
        />
      )}

      {/* Company scope only, and only once there is a roster to narrow — the
          controls above an empty page would be furniture. */}
      {isCompany && allMembers.length > 0 && (
        <TeamFilterPanel
          filters={filters}
          onChange={setFilters}
          search={search}
          onSearchChange={setSearch}
        />
      )}

      {isLoading && <TeamSkeleton />}

      {isError && (
        <ErrorState
          message={error instanceof Error ? error.message : "Could not load the team"}
          onRetry={() => {
            void refetch();
          }}
        />
      )}

      {data && !data.connected && <NotConnectedNotice />}

      {/* The switch above stays rendered, so an admin with no reports of their
          own is one click from the report they came for (AC-914). */}
      {/* "Nobody matches" is a different fact from "nobody is here", and saying
          the second under a filter would be untrue — there ARE active people,
          they are just not these. */}
      {data && members.length === 0 && filtering && (
        <EmptyState
          icon={Users}
          title="Nobody matches these filters"
          description={`None of the ${allMembers.length} people on record match what you have selected.`}
          action={
            <Button
              variant="outline"
              onClick={() => {
                setFilters(EMPTY_TEAM_FILTERS);
                setSearch("");
              }}
            >
              Clear filters
            </Button>
          }
        />
      )}

      {data && members.length === 0 && !filtering && (
        <EmptyState
          icon={Users}
          title={isCompany ? "Nobody to show" : "No direct reports"}
          description={
            isCompany
              ? "There are no active people on record."
              : isSelf
                ? "Nobody reports to you right now."
                : "Nobody reports to this person right now."
          }
        />
      )}

      {data && members.length > 0 && (
        <>
          {/* Only when connected: `NotConnectedNotice` above already explains a
              missing NOS, and two "can't check" messages read as a fault. */}
          {data.connected && (
            <TeamAttention
              attention={attention}
              leaveKnown={data.leaveKnown}
              unassessed={unassessedReports(members)}
              // NOT narrowed by scope: `isSelf` here only sets the origin
              // breadcrumb, so a person opened from company scope must still
              // come back to this page.
              isSelf={isSelf}
              scope={shown}
              narrowed={filtering}
            />
          )}
          <TeamTable members={members} labels={labels} current={current} isSelf={isSelf} />
        </>
      )}
    </div>
  );
}

function TeamSkeleton() {
  return (
    <div className="space-y-2">
      <Skeleton className="h-10 w-full" />
      {[0, 1, 2, 3, 4].map((i) => (
        <Skeleton key={i} className="h-14 w-full" />
      ))}
    </div>
  );
}

/**
 * Shown when the server has no gateway credentials. Deliberately not an error
 * and deliberately not a replacement for the table: the roster is Enable data
 * and is still worth showing, it just has no NOS figures against it.
 */
function NotConnectedNotice() {
  return (
    <div className="flex items-start gap-2 rounded-lg border bg-muted/40 p-3 text-sm">
      <PlugZap className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
      <p className="text-muted-foreground">
        <span className="font-medium text-foreground">Not connected to NOS.</span> Guidance and
        logged time are read live from the Nymbl Gateway, which this environment doesn't have access
        to. The team is listed below without figures.
      </p>
    </div>
  );
}

function TeamTable({
  members,
  labels,
  current,
  isSelf,
}: {
  members: TeamMemberSummary[];
  labels: { month: string; label: string }[];
  current: string;
  isSelf: boolean;
}) {
  const months = labels.map((l) => l.month);
  const [period, setPeriod] = useState(() => defaultLoggedMonth(months));
  const periodLabel = labels.find((l) => l.month === period)?.label ?? period;
  // A month still to come holds booked leave, not logged work — so the heading
  // changes word rather than reporting bookings as time already spent.
  const booked = isBookedPeriod(period);

  return (
    <div className="rounded-xl border">
      {/* The table is wider than a laptop viewport once both column groups are
          in — scroll it rather than letting the page body scroll sideways. */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <caption className="sr-only">
            Direct reports with guided hours by month and logged hours by category
          </caption>
          <thead>
            <tr className="border-b">
              <th
                scope="col"
                rowSpan={2}
                className="sticky left-0 z-10 bg-card px-4 py-2 text-left font-semibold"
              >
                Person
              </th>
              <th
                scope="colgroup"
                colSpan={labels.length}
                className="border-l px-2 py-1.5 text-center font-semibold"
              >
                Guided hours
              </th>
              <th
                scope="colgroup"
                colSpan={TIME_BUCKETS.length + 1}
                className="border-l px-2 py-1 text-center font-semibold"
              >
                <span className="inline-flex items-center gap-2">
                  {booked ? "Booked hours" : "Logged hours"}
                  {/* The period lives IN the heading. Unstated, these columns
                      summed six months and blended old actuals with holiday
                      nobody had taken yet — the heading said only "Logged". */}
                  <span className="inline-flex overflow-hidden rounded-md border">
                    {months.map((m) => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setPeriod(m)}
                        aria-pressed={m === period}
                        className={cn(
                          "px-1.5 py-0.5 font-medium text-2xs",
                          m === period
                            ? "bg-primary/15 text-foreground"
                            : "text-muted-foreground hover:bg-muted",
                        )}
                      >
                        {labels.find((l) => l.month === m)?.label ?? m}
                      </button>
                    ))}
                  </span>
                </span>
              </th>
            </tr>
            <tr className="border-b bg-muted/40 text-2xs">
              {labels.map((l, i) => (
                <th
                  key={l.month}
                  scope="col"
                  className={cn(
                    "px-1.5 py-1.5 align-bottom text-right font-medium text-muted-foreground",
                    i === 0 && "border-l",
                    // The highlight follows the SELECTED period, so one month is
                    // in focus across both halves of the table. The current
                    // month keeps an unmuted label so "now" is still findable —
                    // the under-guided marks are read relative to it.
                    l.month === current && "text-foreground",
                    l.month === period && "bg-primary/10 text-foreground",
                  )}
                >
                  {l.label}
                </th>
              ))}
              {TIME_BUCKETS.map((bucket, i) => (
                <th
                  key={bucket}
                  scope="col"
                  className={cn(
                    "px-2 py-1.5 align-bottom text-right font-medium text-muted-foreground",
                    i === 0 && "border-l",
                  )}
                >
                  {TIME_BUCKET_LABELS[bucket]}
                </th>
              ))}
              <th
                scope="col"
                className="px-2 py-1.5 align-bottom text-right font-medium text-muted-foreground"
              >
                Billable %
              </th>
            </tr>
          </thead>
          <tbody>
            {members.map((member) => (
              <MemberRow
                key={member.personId}
                member={member}
                current={current}
                period={period}
                isSelf={isSelf}
              />
            ))}
          </tbody>
        </table>
      </div>

      <p className="border-t px-4 py-2 text-xs text-muted-foreground">
        A month guided under {UNDER_GUIDED_PERCENT}% of capacity is flagged, from the current month
        on — past months are history.{" "}
        <strong className="font-medium">Logged hours show one month</strong>, {periodLabel} here,
        and open on the last complete one; months still to come hold booked leave rather than logged
        work, so they aren't offered. Billable % is the billable share of that month's worked time;
        leave is excluded from it, so time off never reads as under-performance. Hover a leave
        column for its make-up.
      </p>
    </div>
  );
}

function MemberRow({
  member,
  current,
  period,
  isSelf,
}: {
  member: TeamMemberSummary;
  current: string;
  /** The month the logged columns describe (`YYYY-MM`). */
  period: string;
  isSelf: boolean;
}) {
  const time = member.timeByMonth[period] ?? emptyTimeTotals();
  const billablePercent = billableSharePercent(time);

  return (
    <tr className="border-b last:border-b-0 hover:bg-muted/30">
      <th scope="row" className="sticky left-0 z-10 bg-card px-4 py-1.5 text-left font-normal">
        <Link
          to="/dashboard/persons/$personId"
          params={{ personId: member.personId }}
          // Only the viewer's own team page has a stable address a page origin
          // can encode, so only it offers a breadcrumb back.
          search={isSelf ? pageOriginSearch("team") : undefined}
          className="flex items-center gap-2.5 hover:underline"
        >
          <PersonAvatar
            name={member.name}
            seed={member.personId}
            size="sm"
            imageUrl={member.avatarUrl}
          />
          {/* Name and title on ONE line: stacked, they set a 52px row floor
              and a manager saw a single report without scrolling. */}
          <span className="flex min-w-0 items-baseline gap-2">
            <span className="shrink-0 font-medium">{member.name}</span>
            {member.title && (
              <span className="truncate text-muted-foreground text-xs">{member.title}</span>
            )}
          </span>
        </Link>
      </th>

      {member.months.map((month, i) => (
        <GuidanceCell
          key={month.month}
          month={month}
          current={current}
          period={period}
          first={i === 0}
        />
      ))}

      {TIME_BUCKETS.map((bucket, i) => (
        <TimeCell key={bucket} bucket={bucket} time={time} first={i === 0} />
      ))}

      <td className={cn("px-2 py-1.5 text-right", FIGURE)}>
        {billablePercent !== null ? `${billablePercent}%` : <span className={EMPTY}>—</span>}
      </td>
    </tr>
  );
}

function GuidanceCell({
  month,
  current,
  period,
  first,
}: {
  month: TeamGuidanceMonth;
  current: string;
  /** The selected month, which is what the column highlight follows. */
  period: string;
  first: boolean;
}) {
  // Only flag the current month and later. A past month being under-guided is
  // history a manager can do nothing about, and flagging it would leave most
  // rows permanently lit up — which would train people to ignore the colour.
  const flag = month.month >= current && isUnderGuided(month);

  return (
    <td
      className={cn(
        "px-1.5 py-1.5 text-right",
        FIGURE,
        first && "border-l",
        month.month === period && "bg-primary/5",
        flag && "text-warning-foreground",
      )}
    >
      {month.guidedHours !== null ? (
        <span className="inline-flex items-center gap-1">
          {flag && <TriangleAlert className="size-3" aria-label="Under-guided" />}
          {hoursValue(month.guidedHours)}
        </span>
      ) : (
        <span className={cn(!flag && EMPTY)}>
          {flag ? (
            <span className="inline-flex items-center gap-1">
              <TriangleAlert className="size-3" aria-label="No guidance set" />—
            </span>
          ) : (
            "—"
          )}
        </span>
      )}
    </td>
  );
}

function TimeCell({
  bucket,
  time,
  first,
}: {
  bucket: TimeBucket;
  time: TimeTotals;
  first: boolean;
}) {
  const hours = time[bucket];
  const breakdown = time.breakdown[bucket];
  const share = totalLogged(time) > 0 ? Math.round((hours / totalLogged(time)) * 100) : null;

  const value = hours > 0 ? hoursValue(hours) : <span className={EMPTY}>&mdash;</span>;

  // Only worth a hover when there is something underneath to reveal. A single
  // constituent type equal to the column itself tells the reader nothing.
  if (breakdown.length === 0) {
    return <td className={cn("px-2 py-1.5 text-right", FIGURE, first && "border-l")}>{value}</td>;
  }

  return (
    <td className={cn("px-2 py-1.5 text-right", FIGURE, first && "border-l")}>
      <SimpleTooltip
        content={
          <div className="space-y-0.5">
            {breakdown.map((entry) => (
              <div key={entry.label} className="flex justify-between gap-3">
                <span>{entry.label}</span>
                <span className="tabular-nums">{hoursValue(entry.hours)}h</span>
              </div>
            ))}
            {share !== null && (
              <div className="border-t pt-0.5 text-muted-foreground">{share}% of logged time</div>
            )}
          </div>
        }
      >
        <span className="underline decoration-dotted underline-offset-4">{value}</span>
      </SimpleTooltip>
    </td>
  );
}
